import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import Seo from "../components/Seo";

export default function Pricing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setPlans(data));
    if (user?.dealer_id) {
      api.get(`/dealers/${user.dealer_id}`).then(({ data }) =>
        setCurrentPlan(data.subscription_plan || "basic")
      ).catch(() => {});
    }
  }, [user]);

  const choose = async (planId) => {
    if (!user) { navigate("/login?next=/pricing"); return; }
    if (user.role !== "dealer") {
      toast.error("Only dealers can subscribe. Please register as a dealer first.");
      navigate("/dealer-dashboard");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/subscribe", { plan: planId });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.mocked) {
        toast.success(`MOCKED payment success — plan upgraded to ${planId}`);
        setCurrentPlan(planId);
      }
    } catch (e) {
      toast.error("Payment failed: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const planMap = {
    basic: { features: [t("pricing.basic.f1"), t("pricing.basic.f2"), t("pricing.basic.f3")], color: "slate" },
    pro: { features: [t("pricing.pro.f1"), t("pricing.pro.f2"), t("pricing.pro.f3")], color: "primary", popular: true },
    premium: { features: [t("pricing.premium.f1"), t("pricing.premium.f2"), t("pricing.premium.f3"), t("pricing.premium.f4")], color: "accent" },
  };

  return (
    <div data-testid="pricing-page" className="bg-slate-50 min-h-screen">
      <Seo title={t("pricing.title")} description={t("pricing.subtitle")} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-cm-primary tracking-tight">{t("pricing.title")}</h1>
          <p className="mt-3 text-slate-600">{t("pricing.subtitle")}</p>
          <p className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> {t("pricing.payment_provider")}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 stagger">
          {plans.map((plan) => {
            const meta = planMap[plan.id] || planMap.basic;
            const isCurrent = currentPlan === plan.id;
            const isPopular = meta.popular;
            return (
              <div key={plan.id} data-testid={`plan-${plan.id}`}
                   className={`relative bg-white rounded-2xl p-7 border-2 transition-all ${isPopular ? 'border-cm-accent shadow-xl shadow-cm-accent/10 scale-[1.02]' : 'border-slate-200 hover:border-slate-300'}`}>
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cm-accent text-white text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" /> Most popular
                  </span>
                )}
                <h3 className="font-display font-extrabold text-2xl text-cm-primary capitalize">{t(`dealer.plan.${plan.id}`)}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display font-extrabold text-4xl text-cm-primary">${plan.price}</span>
                  <span className="text-sm text-slate-500">{t("pricing.month")}</span>
                </div>
                <ul className="mt-5 space-y-2.5">
                  {meta.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => choose(plan.id)}
                  disabled={loading || isCurrent}
                  data-testid={`choose-${plan.id}`}
                  className={`mt-6 w-full px-5 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
                    isCurrent ? 'bg-emerald-100 text-emerald-700' :
                    isPopular ? 'bg-cm-accent text-white hover:bg-cm-accent-hover' :
                    'bg-cm-primary text-white hover:bg-cm-primary-deep'
                  } press-scale disabled:opacity-60`}
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isCurrent ? t("pricing.current") : t("pricing.choose")}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-cm-primary text-white rounded-2xl p-7 md:p-10 flex flex-col md:flex-row items-center gap-6">
          <div className="flex-1">
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">{t("pricing.featured_upsell")}</h3>
            <p className="mt-2 text-slate-300 max-w-lg">{t("pricing.featured_upsell_d")}</p>
          </div>
          <button
            onClick={() => navigate(user?.dealer_id ? "/dealer-dashboard" : "/login")}
            data-testid="featured-upsell-cta"
            className="px-6 py-3 rounded-xl bg-cm-accent hover:bg-cm-accent-hover text-white font-semibold press-scale"
          >
            {t("pricing.featured_buy")}
          </button>
        </div>
      </div>
    </div>
  );
}
